import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AI_PDF_CONST from "../../util/PdfAiConst";
import { fetchTenants } from "../../api/tenantApi";
import { fetchOnboardingStatus } from "../../api/onboardingApi";
import PageHeader from "../../components/admin/PageHeader";
import LoadingState from "../../components/admin/LoadingState";
import ErrorState from "../../components/admin/ErrorState";
import StatusBadge from "../../components/admin/StatusBadge";

const OnboardingListPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const tenantRes = await fetchTenants();
        const tenantList = Array.isArray(tenantRes)
          ? tenantRes
          : tenantRes?.data?.items || tenantRes?.data || [];

        const enriched = await Promise.all(
          tenantList.map(async (tenant) => {
            let onboarding = null;
            try {
              const obRes = await fetchOnboardingStatus(tenant.id);
              onboarding = obRes?.data || obRes || null;
            } catch {
              // skip
            }
            const steps = onboarding?.steps || [];
            const completed = steps.filter((s) => s.status === "completed").length;
            const total = steps.length || 4;
            const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
            return {
              ...tenant,
              currentStep: onboarding?.current_step || "not_started",
              progress,
              completed: onboarding?.completed || false,
            };
          })
        );
        setRows(enriched);
      } catch (err) {
        setError(err.message || "Failed to load onboarding data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <LoadingState label="Loading onboarding data..." />;
  if (error) return <ErrorState description={error} />;

  const manageLink = (id) => AI_PDF_CONST.ROUTE.ADMIN_TENANT_DETAIL.replace(":tenant_id", id) + "?tab=onboarding";

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Platform admin"
        title="Onboarding"
        description="Track onboarding progress across all tenants."
      />

      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50 text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Tenant Name</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Current Step</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Progress</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 text-right">Quick Manage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((tenant) => (
              <tr key={tenant.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-5 py-4 font-medium text-slate-900">{tenant.name}</td>
                <td className="px-5 py-4 text-slate-700 capitalize">{tenant.currentStep.replace(/_/g, " ")}</td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-24 rounded-full bg-slate-100">
                      <div className="h-2 rounded-full bg-[#096dea]" style={{ width: `${tenant.progress}%` }}></div>
                    </div>
                    <span className="text-xs font-semibold text-slate-500">{tenant.progress}%</span>
                  </div>
                </td>
                <td className="px-5 py-4">
                  <StatusBadge status={tenant.completed ? "completed" : tenant.currentStep === "not_started" ? "draft" : "in_progress"} />
                </td>
                <td className="px-5 py-4 text-right">
                  <Link
                    to={manageLink(tenant.id)}
                    className="inline-flex items-center rounded-lg bg-[#096dea] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#0959bb]"
                  >
                    Manage →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No tenants found.</div>
        ) : null}
      </div>
    </div>
  );
};

export default OnboardingListPage;
